import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { InvoiceService } from '@galaxy/billing';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ACCOUNT_GROUP_ID_TOKEN,
  AppOptionsKeys,
  ConversationDetail,
  ConversationMessage,
  ConversationTitleInfo,
  CreateContactState,
  GROUP_ID_TOKEN,
  HostAppInterface,
  InboxNamespace,
  isAccountGroupAndCustomerConversation,
  isSubjectParticipantInHierarchy,
  KabobDynamicButton,
  ParticipantService,
  resolveParticipantTitle,
  RouteConfig,
  SendMessage,
  USER_ID_TOKEN,
} from '@galaxy/conversation/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { toFirestoreId } from '@galaxy/conversation/core';
import { formatAsReadonlyPhone, parsePhoneNumber, PAGE_ROUTES } from '@galaxy/crm/static';
import { PaymentLinkItem } from '@vendasta/billing';
import { CustomerSource } from '@vendasta/protos/billing/v1';
import {
  ConversationChannel,
  GlobalParticipantType,
  Participant,
  ParticipantType,
  PlatformLocation,
  SubjectParticipant,
} from '@vendasta/conversation';
import { newAccountServiceContext, SSOService } from '@vendasta/sso';
import { combineLatest, Observable, of, shareReplay } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { UserService, AssociatedUserInterface } from '@vendasta/business-center';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ConversationBusinessAppService implements HostAppInterface {
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly groupId$ = inject(GROUP_ID_TOKEN);
  private readonly userId$ = inject(USER_ID_TOKEN);
  private readonly userId = toSignal(this.userId$);
  private readonly accountGroupId = toSignal(this.accountGroupId$);
  private readonly groupId = toSignal(this.groupId$);
  readonly isMultiLocation$ = this.groupId$.pipe(map((groupId) => !!groupId));
  readonly isMultiLocation = toSignal(this.isMultiLocation$);

  private readonly namespace$ = combineLatest([this.isMultiLocation$, this.groupId$, this.accountGroupId$]).pipe(
    map(([isMultiLocation, groupId, accountGroupId]) => {
      return isMultiLocation
        ? { namespaceType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_GROUP, id: groupId }
        : { namespaceType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP, id: accountGroupId };
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  constructor(
    private participantService: ParticipantService,
    private router: Router,
    private invoiceService: InvoiceService,
    private ssoService: SSOService,
    private userService: UserService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
  ) {}

  getConversationTitleInfo(conversationDetail: ConversationDetail): ConversationTitleInfo {
    if (this.isMultiLocation()) {
      return this.getTitleForMultiLocation(conversationDetail);
    } else if (this.isPrivateChat(conversationDetail) && this.userId() !== '') {
      return this.getTitleForPrivateChat(conversationDetail, this.userId());
    } else {
      return this.getTitle(conversationDetail);
    }
  }

  private getTitle(conversationDetail: ConversationDetail): ConversationTitleInfo {
    const recipient = this.getRecipient(conversationDetail);
    const conversationTitleInfo = {} as ConversationTitleInfo;

    if (recipient) {
      switch (recipient.participantType) {
        case ParticipantType.PARTICIPANT_TYPE_CUSTOMER:
          if (recipient.isParticipantInternalInfoDeleted) {
            conversationTitleInfo.title = this.translateService.instant('INBOX.ERROR.DELETED_CUSTOMER');
          } else {
            const parsedPhoneNumber = recipient?.phoneNumber ? parsePhoneNumber(recipient?.phoneNumber) : '';
            const phoneNumber = parsedPhoneNumber ? formatAsReadonlyPhone(parsedPhoneNumber) : '';

            const secondaryInfo = phoneNumber ? phoneNumber : recipient?.email;

            conversationTitleInfo.title = resolveParticipantTitle(recipient);
            conversationTitleInfo.subtitle = secondaryInfo;

            if (recipient?.phoneNumber && recipient?.email) {
              conversationTitleInfo.subtitle = phoneNumber + ' - ' + recipient?.email;
            }
          }
          break;
        default:
          conversationTitleInfo.title = recipient.name;
          break;
      }
    }

    return conversationTitleInfo;
  }

  private getTitleForMultiLocation(conversationDetail: ConversationDetail): ConversationTitleInfo {
    const accountGroup = conversationDetail.participants.find(
      (subjectParticipant) => subjectParticipant?.participantType === ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
    );

    const titleInfo = this.getTitle(conversationDetail);
    titleInfo.title = `${accountGroup?.name} - ${titleInfo.title}`;
    return titleInfo;
  }

  private isPrivateChat(
    conversationDetail: ConversationDetail,
  ): boolean {
    return conversationDetail.participants?.every(
      (p) => p?.participantType
        === ParticipantType.PARTICIPANT_TYPE_IAM_USER,
    );
  }

  private getTitleForPrivateChat(conversationDetail: ConversationDetail, userId: string): ConversationTitleInfo {
    const conversationTitleInfo = {} as ConversationTitleInfo;
    const otherParticipant = conversationDetail.participants?.find(
      (participant) => participant?.internalParticipantId !== userId,
    );
    if (otherParticipant) {
      conversationTitleInfo.title = otherParticipant?.name || otherParticipant?.email;
      conversationTitleInfo.subtitle =
        conversationTitleInfo.title !== otherParticipant?.email ? otherParticipant?.email : '';
    }
    return conversationTitleInfo;
  }

  getRecipient(conversationDetail: ConversationDetail): Participant | null {
    const participants = conversationDetail?.participants;
    let matchesType: (p: Participant) => boolean;

    switch (conversationDetail?.conversation?.channel) {
      case ConversationChannel.CONVERSATION_CHANNEL_INTERNAL:
        matchesType = (p) => p.participantType === ParticipantType.PARTICIPANT_TYPE_PARTNER;
        break;
      case ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT:
        matchesType = (p) =>
          p.participantType === ParticipantType.PARTICIPANT_TYPE_ANONYMOUS ||
          p.participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER;
        break;
      case ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_CHAT:
        matchesType = (p) => p.participantType !== ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP;
        break;
      default:
        matchesType = (p) => p.participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER;
        break;
    }

    return participants?.find((p) => p.isSubjectParticipant && matchesType(p));
  }

  private getSender(participants: Participant[]): Participant | null {
    let sender = participants?.find(
      (participant) => participant?.participantType === ParticipantType.PARTICIPANT_TYPE_IAM_USER,
    );

    if (!sender) {
      sender = participants?.find(
        (participant) => participant?.participantType === ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
      );
    }

    return sender;
  }

  isSenderFromOrganization(message: ConversationMessage): boolean {
    switch (message.sender?.participantType) {
      case ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP:
      case ParticipantType.PARTICIPANT_TYPE_OPENAI_BOT:
        return true;
      case ParticipantType.PARTICIPANT_TYPE_IAM_USER:
        return (
          isSubjectParticipantInHierarchy(
            message.sender?.namespaceHierarchy,
            GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_GROUP,
          ) ||
          isSubjectParticipantInHierarchy(
            message.sender?.namespaceHierarchy,
            GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
          )
        );
      default:
        return false;
    }
  }

  isYourExpert(conversationDetail: ConversationDetail): boolean {
    return (
      this.getRecipient(conversationDetail)?.internalParticipantId !== '' &&
      conversationDetail.conversation.channel === ConversationChannel.CONVERSATION_CHANNEL_INTERNAL &&
      this.getRecipient(conversationDetail)?.participantType === ParticipantType.PARTICIPANT_TYPE_PARTNER
    );
  }

  isRecipientInternalInfoDeleted(conversationDetail: ConversationDetail): boolean {
    return this.getRecipient(conversationDetail)?.isParticipantInternalInfoDeleted ?? false;
  }

  buildSubjectParticipant(partnerId: string, subjectParticipant: SubjectParticipant): Participant {
    switch (subjectParticipant?.participantType) {
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP:
        return this.participantService.buildAccountGroup(partnerId, subjectParticipant?.internalParticipantId);
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER:
        return this.participantService.buildPartner(subjectParticipant?.internalParticipantId);
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_VENDOR:
        return this.participantService.buildVendor(subjectParticipant?.internalParticipantId);
      default:
        return {} as Participant;
    }
  }

  buildParticipants(currentIAMParticipant: Participant, participants: SubjectParticipant[]): Observable<Participant[]> {
    const partnerId = currentIAMParticipant?.partnerId;
    const participantList: Participant[] = [];

    participantList.push(currentIAMParticipant);
    participants.forEach((participant) => {
      participantList.push(this.buildSubjectParticipant(partnerId, participant));
    });

    return of(participantList);
  }

  buildSendMessageParams(
    currentIAMParticipant: Participant,
    subjectParticipants: SubjectParticipant[],
    channel: ConversationChannel,
  ): Observable<SendMessage> {
    return this.buildParticipants(currentIAMParticipant, subjectParticipants).pipe(
      map((participants) => ({
        participants: participants,
        location: PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP,
        channel: channel,
      })),
    );
  }

  redirectToInternalConversation(
    currentIAMParticipant: Participant,
    recipientSubjectParticipant: SubjectParticipant[],
    channel: ConversationChannel,
  ): void {
    this.buildSendMessageParams(currentIAMParticipant, recipientSubjectParticipant, channel)
      .pipe(take(1))
      .subscribe((params) => {
        if (!currentIAMParticipant?.accountGroupId) {
          console.error('current IAM Participant does not have accountGroupId');
          return;
        }
        if (this.router.url.includes('inbox')) {
          this.router.navigate([`/account/location/${currentIAMParticipant?.accountGroupId}/inbox/send-message`], {
            state: params,
          });
        } else {
          this.router.navigate([`/account/location/${currentIAMParticipant?.accountGroupId}/inbox/home`]).then(() =>
            this.router.navigate([`/account/location/${currentIAMParticipant?.accountGroupId}/inbox/send-message`], {
              state: params,
            }),
          );
        }
      });
  }

  buildConversationURLByCurrentURL(conversationID: string): string {
    if (this.isMultiLocation()) {
      return `/account/brands/${this.groupId()}/inbox/channel/${conversationID}`;
    } else {
      return `/account/location/${this.accountGroupId()}/inbox/channel/${conversationID}`;
    }
  }

  createPaymentLink(
    conversationDetail: ConversationDetail,
    paymentLinkItems: PaymentLinkItem[],
    due: Date,
  ): Observable<string> {
    if (!isAccountGroupAndCustomerConversation(conversationDetail.conversation)) {
      // only messages between customer and account group can have payment links
      this.snackbarService.openErrorSnack('INBOX.ERROR.CONVERSATION_DETAILS');
      return of('');
    }
    const customer = conversationDetail?.conversation?.subjectParticipantKey?.subjectParticipants.find(
      (p) => p.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_CUSTOMER,
    );
    const sender = this.getSender(conversationDetail.participants);
    return this.invoiceService
      .createPaymentLink(
        sender.accountGroupId,
        customer.internalParticipantId,
        paymentLinkItems,
        'USD',
        due,
        CustomerSource.CUSTOMER_SOURCE_CRM_CONTACT,
      )
      .pipe(
        mergeMap((invoiceId) => {
          if (!invoiceId) {
            return of('');
          }
          return this.ssoService.getEntryUrl('VBC', newAccountServiceContext(sender.accountGroupId), {
            path: `/invoice/${sender.accountGroupId}/${invoiceId}`,
            skipGatewayAuthentication: true,
          });
        }),
        catchError(() => {
          return of('');
        }),
      );
  }

  shortenLink(_link: string, _accountGroupId: string): Observable<string> {
    throw new Error('Not implemented');
  }

  calculatePaymentLinkTax(_conversationDetail: ConversationDetail, _amount: number): Observable<number> {
    throw new Error('Not implemented');
  }

  navigateToCreateContact(state: CreateContactState, routes: RouteConfig): void {
    this.router.navigate([routes.root, 'inbox', 'create-contact'], {
      state,
      queryParamsHandling: 'preserve',
    });
  }

  getKabobAvailableActions(conversationDetail: ConversationDetail): Observable<KabobDynamicButton[]> {
    const accountGroupId = conversationDetail.participants.find(
      (participant) =>
        participant?.isSubjectParticipant &&
        participant?.participantType === ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
    )?.internalParticipantId;
    const actionsAvailable: KabobDynamicButton[] = [];
    if (accountGroupId) {
      const customerRecipient = this.getRecipient(conversationDetail);
      if (
        customerRecipient &&
        customerRecipient.participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER &&
        !customerRecipient.isParticipantInternalInfoDeleted
      ) {
        const editContactAction: KabobDynamicButton = {
          title: 'INBOX.SENDER.VIEW_CONTACT',
          action: () => this.viewContact(conversationDetail),
          posthogLabel: 'clicked-view-contact',
        };
        actionsAvailable.push(editContactAction);
      }
    }
    return of(actionsAvailable);
  }

  getAppOptions(): { [key in AppOptionsKeys]?: boolean } {
    return {
      is_multilocation: this.isMultiLocation(),
    };
  }

  getUsers(accountGroupId: string): Observable<AssociatedUserInterface[]> {
    return this.userService.listUsersForBusiness(accountGroupId).pipe(map((response) => response.associatedUsers));
  }

  getNamespace(): Observable<InboxNamespace> {
    return this.namespace$;
  }

  viewContact(conversationDetail: ConversationDetail): void {
    const accountGroupId = this.accountGroupId();
    const groupId = this.groupId();

    const firestoreDocId = toFirestoreId(conversationDetail.conversation.conversationId);
    if (!firestoreDocId) {
      this.snackbarService.openErrorSnack('INBOX.DETAILS.LOADING_ERROR');
      return;
    }

    const subjectParticipants = conversationDetail.conversation?.subjectParticipantKey?.subjectParticipants;
    if (!subjectParticipants?.length || (!accountGroupId && !groupId)) {
      this.snackbarService.openErrorSnack('INBOX.TOPBAR.ERROR_LOADING_RECIPIENT');
      return;
    }

    const contactParticipant = subjectParticipants.find(
      (participant) => participant.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_CUSTOMER,
    );
    if (!contactParticipant || !contactParticipant.internalParticipantId) {
      this.snackbarService.openErrorSnack('INBOX.TOPBAR.ERROR_LOADING_RECIPIENT');
      return;
    }

    let routeTemplate = '';
    let returnUrl = '';

    if (this.isMultiLocation()) {
      const accountGroupParticipant = subjectParticipants.find(
        (participant) => participant.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
      );
      if (!accountGroupParticipant?.internalParticipantId) {
        this.snackbarService.openErrorSnack('INBOX.TOPBAR.ERROR_LOADING_RECIPIENT');
        return;
      }
      returnUrl = `/account/brands/${groupId}/inbox/channel/${firestoreDocId}`;
      routeTemplate = `/account/brands/${groupId}/contacts/${accountGroupParticipant.internalParticipantId}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
    } else {
      returnUrl = `/account/location/${accountGroupId}/inbox/channel/${firestoreDocId}`;
      routeTemplate = `/account/location/${accountGroupId}/crm/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
    }

    this.router.navigate([routeTemplate.replace(':crmObjectId', contactParticipant.internalParticipantId)], {
      queryParams: { returnUrl },
    });
  }
}
